import React, { useMemo, useState } from 'react';
import { X, CheckCircle2, Lock, Mic, Layers, MessageSquare, Music, Sparkles, ShieldCheck } from 'lucide-react';
import { api } from '../app/api';
import { logger } from '../lib/logger';
import { useAuth } from '../app/AuthContext';
import { useLockerRoomEntitlement } from '../hooks/useLockerRoomEntitlement';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';

/**
 * LockerRoomPassModal
 * ---------------------------------------------------------------------------
 * Conversion modal for The Locker Room Pass ($29.99/mo · $299/yr).
 *
 * Behavior (Phase 20B):
 *   - Reads auth state via `useAuth()`:
 *       * Authed user → submit with no email/name fields (uses session identity).
 *       * Guest      → captures email + optional name; backend creates a
 *                      passwordless stub user and sends a password-setup link.
 *   - Reads entitlement via `useLockerRoomEntitlement()`:
 *       * Premium-tier users see an "already included" state (their $199 1:1
 *         Coach plan auto-unlocks Locker Room Pass).
 *       * Active Pass holders see an "already a member" state.
 *   - On submit, POSTs to `/api/billing/locker-room-pass/checkout` and (on
 *     success) redirects the browser to the Stripe Checkout URL.
 *   - Failure modes route through `toast` with a typed error message.
 */

const MONTHLY_PRICE_CENTS = 2999;
const ANNUAL_PRICE_CENTS = 29900;
const ANNUAL_MONTHLY_EQUIV_CENTS = Math.round(ANNUAL_PRICE_CENTS / 12);
const ANNUAL_SAVINGS_CENTS = MONTHLY_PRICE_CENTS * 12 - ANNUAL_PRICE_CENTS;

function fmtUsd(cents, { showCents = true } = {}) {
  const dollars = cents / 100;
  return showCents ? `$${dollars.toFixed(2)}` : `$${Math.round(dollars)}`;
}

const PASS_FEATURES = [
  { icon: Music, title: 'All curated playlists', detail: 'Every clean-lyrics Spotify mix Coach Lewis curates \u2014 pre-game, in-the-zone, recovery, wind-down. New drops monthly.' },
  { icon: Layers, title: 'Full Apex Six deck', detail: 'All six pre-game protocols unlocked: Composure, Confidence, Awareness, Focus, Resilience, Preparation.' },
  { icon: MessageSquare, title: 'Full Apex AI Coach', detail: 'Unlimited chat, pre-game prep generator, journal, progress dashboard, and video tools \u2014 the complete coaching app, included with your Pass.' },
  { icon: Mic, title: 'Weekly audio drops', detail: '90-second locker-room talks from Coach Lewis. Game-day pump-ups, post-loss resets, mid-season grind fuel.' },
  { icon: Sparkles, title: 'Founders-locked pricing', detail: 'Lock in launch pricing for life. No surprise increases. No upsells.' },
];

const SOURCE_LABELS = {
  'playlist': 'Unlock all Locker Room playlists',
  'extended-deck': 'Unlock the full Apex Six deck',
  'audio-drops': 'Unlock Coach Lewis audio drops',
  'ai-coach-full': 'Unlock the full Apex AI Coach',
  'founders-pass': 'Join the Founders Pass',
  'floating-player': 'Unlock the full music library',
  'locker-room-pass': 'Unlock The Locker Room',
};

function sourceLabel(source) {
  if (!source) return 'Unlock The Locker Room';
  const root = source.split(':')[0];
  return SOURCE_LABELS[source] || SOURCE_LABELS[root] || 'Unlock The Locker Room';
}

export function LockerRoomPassModal({ source, onClose }) {
  const { user } = useAuth();
  const { source: entitlementSource, isPro } = useLockerRoomEntitlement();
  const [cycle, setCycle] = useState('annual'); // 'monthly' | 'annual'
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [submitting, setSubmitting] = useState(false);

  const trigger = useMemo(() => sourceLabel(source), [source]);
  const isAuthed = Boolean(user?.user_id);

  // Already-active states — show informational modal instead of the pricing form.
  const alreadyActive = isPro;
  const alreadyActiveReason = entitlementSource === 'premium'
    ? 'Your 1:1 Coach Premium plan already unlocks The Locker Room. Welcome inside.'
    : 'You’re an active Locker Room Pass member. Everything is unlocked for you.';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthed && !email.trim()) {
      toast.error('Email is required');
      return;
    }
    setSubmitting(true);
    try {
      const result = await api.post('/api/billing/locker-room-pass/checkout', {
        billing_cycle: cycle,
        origin_url: window.location.origin,
        // For authed users the backend ignores these; for guests they’re required.
        email: isAuthed ? undefined : email.trim(),
        name: isAuthed ? undefined : (name.trim() || undefined),
        source: source || 'modal',
      });
      if (result?.already_active) {
        toast.success(result.message || 'You already have access.');
        setSubmitting(false);
        return;
      }
      if (!result?.url) {
        throw new Error('Stripe did not return a checkout URL');
      }
      // Redirect to Stripe Checkout. We intentionally do not set submitting:false
      // because we want the spinner to stay visible during the navigation hop.
      window.location.assign(result.url);
    } catch (err) {
      logger.error('LockerRoomPassModal: checkout failed', err);
      toast.error(err?.message || 'Could not start checkout. Try again?');
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-start sm:items-center justify-center bg-black/75 backdrop-blur-sm overflow-y-auto p-3 sm:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="locker-pass-modal"
    >
      <div className="w-full max-w-[520px] bg-[#0F1314] text-white rounded-2xl shadow-2xl ring-1 ring-white/10 overflow-hidden my-4 sm:my-0">
        {/* Header */}
        <div className="relative px-5 sm:px-6 pt-5 pb-4 border-b border-white/8 bg-gradient-to-br from-[#004C54]/30 via-[#0F1314] to-[#0F1314]">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Close"
            data-testid="locker-pass-modal-close"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-white/8 text-[10px] font-mono-accent uppercase tracking-[0.2em] font-semibold text-white/80 mb-3">
            <Lock className="h-3 w-3 text-[#4FBCC7]" />
            Locker Room Pass
          </div>
          <h2 className="font-heading text-[1.55rem] sm:text-[1.85rem] font-bold tracking-tight leading-[1.1] text-white mb-2" data-testid="locker-pass-modal-title">
            {alreadyActive ? 'You’re already inside.' : `${trigger}.`}
          </h2>
          <p className="text-[13.5px] text-white/65 leading-snug max-w-[40ch]">
            {alreadyActive
              ? alreadyActiveReason
              : 'Full library access, the complete Apex Six deck, weekly audio drops, and Coach Lewis live.'}
          </p>
        </div>

        {alreadyActive ? (
          <div className="px-5 sm:px-6 py-8 text-center" data-testid="locker-pass-already-active">
            <div className="mx-auto h-12 w-12 rounded-full bg-[#004C54] flex items-center justify-center mb-4">
              <ShieldCheck className="h-6 w-6 text-white" />
            </div>
            <h3 className="font-heading text-[1.2rem] font-bold mb-2">All access · unlocked.</h3>
            <p className="text-[13.5px] text-white/65 max-w-[36ch] mx-auto leading-snug mb-5">
              Close this and explore — every playlist, every Apex Six card, every drop is open to you.
            </p>
            <Button
              onClick={onClose}
              className="bg-white text-[#0B0C0D] hover:bg-white/90 h-10 px-5 rounded-xl"
              data-testid="locker-pass-already-active-close"
            >
              Take me to the locker
            </Button>
          </div>
        ) : (
          <div className="px-5 sm:px-6 py-5">
            {/* Billing cycle toggle */}
            <div
              role="radiogroup"
              aria-label="Billing cycle"
              className="grid grid-cols-2 gap-2 p-1 rounded-xl bg-black/40 ring-1 ring-white/10 mb-5"
              data-testid="locker-pass-cycle-toggle"
            >
              <button
                type="button"
                role="radio"
                aria-checked={cycle === 'monthly'}
                onClick={() => setCycle('monthly')}
                className={`relative rounded-lg px-3 py-3 text-left transition-colors ${
                  cycle === 'monthly'
                    ? 'bg-[#004C54] text-white ring-1 ring-[#4FBCC7]/40'
                    : 'text-white/65 hover:text-white hover:bg-white/5'
                }`}
                data-testid="locker-pass-cycle-monthly"
              >
                <div className="font-mono-accent uppercase tracking-[0.18em] text-[9.5px] font-semibold mb-1 opacity-80">
                  Monthly
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="font-heading text-[1.35rem] font-bold leading-none">{fmtUsd(MONTHLY_PRICE_CENTS)}</span>
                  <span className="text-[11px] opacity-70">/mo</span>
                </div>
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={cycle === 'annual'}
                onClick={() => setCycle('annual')}
                className={`relative rounded-lg px-3 py-3 text-left transition-colors ${
                  cycle === 'annual'
                    ? 'bg-[#004C54] text-white ring-1 ring-[#4FBCC7]/40'
                    : 'text-white/65 hover:text-white hover:bg-white/5'
                }`}
                data-testid="locker-pass-cycle-annual"
              >
                <div className="absolute -top-2 right-2 inline-flex items-center px-1.5 py-0.5 rounded-full bg-[#4FBCC7] text-[#0B0C0D] text-[9px] font-mono-accent uppercase tracking-[0.16em] font-bold">
                  Save {fmtUsd(ANNUAL_SAVINGS_CENTS, { showCents: false })}
                </div>
                <div className="font-mono-accent uppercase tracking-[0.18em] text-[9.5px] font-semibold mb-1 opacity-80">
                  Annual
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="font-heading text-[1.35rem] font-bold leading-none">{fmtUsd(ANNUAL_PRICE_CENTS, { showCents: false })}</span>
                  <span className="text-[11px] opacity-70">/yr</span>
                </div>
                <div className="text-[10.5px] mt-1 opacity-70">
                  {fmtUsd(ANNUAL_MONTHLY_EQUIV_CENTS)}/mo equivalent
                </div>
              </button>
            </div>

            {/* Feature checklist */}
            <ul className="space-y-2.5 mb-5" data-testid="locker-pass-features">
              {PASS_FEATURES.map((f) => {
                const Icon = f.icon;
                return (
                  <li key={f.title} className="flex gap-2.5">
                    <span className="shrink-0 inline-flex h-7 w-7 items-center justify-center rounded-lg bg-[#004C54]/45 text-[#4FBCC7]">
                      <Icon className="h-3.5 w-3.5" />
                    </span>
                    <div className="leading-tight">
                      <div className="text-[13px] font-semibold text-white">{f.title}</div>
                      <div className="text-[11.5px] text-white/55 leading-snug mt-0.5">{f.detail}</div>
                    </div>
                  </li>
                );
              })}
            </ul>

            {/* Capture form */}
            <form onSubmit={handleSubmit} className="space-y-3" data-testid="locker-pass-form">
              {!isAuthed && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="lrp-name" className="text-[11px] uppercase tracking-[0.16em] font-mono-accent text-white/55 font-semibold">Name</Label>
                    <Input
                      id="lrp-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Coach Lewis"
                      className="bg-black/40 border-white/15 text-white placeholder:text-white/35 h-10"
                      data-testid="locker-pass-input-name"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="lrp-email" className="text-[11px] uppercase tracking-[0.16em] font-mono-accent text-white/55 font-semibold">Email *</Label>
                    <Input
                      id="lrp-email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@school.edu"
                      className="bg-black/40 border-white/15 text-white placeholder:text-white/35 h-10"
                      data-testid="locker-pass-input-email"
                    />
                  </div>
                </div>
              )}
              {isAuthed && (
                <div className="text-[11.5px] text-white/55 rounded-lg bg-white/4 px-3 py-2 leading-snug" data-testid="locker-pass-signed-in-notice">
                  Signed in as <span className="text-white font-semibold">{user?.email}</span>. Your Pass will activate on this account.
                </div>
              )}
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-white text-[#0B0C0D] hover:bg-white/90 h-11 rounded-xl font-semibold disabled:opacity-60"
                data-testid="locker-pass-submit"
              >
                {submitting
                  ? 'Opening secure checkout…'
                  : `Subscribe — ${cycle === 'annual' ? fmtUsd(ANNUAL_PRICE_CENTS, { showCents: false }) + '/yr' : fmtUsd(MONTHLY_PRICE_CENTS) + '/mo'}`}
              </Button>
              <p className="text-[11px] text-white/45 text-center leading-snug">
                Secure checkout via Stripe. Cancel anytime from your dashboard.{' '}
                {!isAuthed && (
                  <span className="block mt-1">No account needed — we’ll set yours up after payment.</span>
                )}
              </p>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

export default LockerRoomPassModal;
